import { Injectable } from '@angular/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { NavigationItem, NavigationLink, NavigationDropdown } from './navigation-item.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { OutlineService } from '../auth/outline.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationLoaderService {
  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);

  private _allItems: NavigationItem[] = [];

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor(
    private readonly layoutService: VexLayoutService,
    private readonly outlineService: OutlineService
  ) {
    this.loadNavigation();
  }

  loadNavigation(): void {
    this._allItems = [
      {
        type: 'subheading',
        label: 'CIC-Home',
        children: [
          {
            type: 'link',
            label: 'Home',
            route: '/cic-home/home',
            icon: 'mat:home',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 1,
            id_vista: 1
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Soluciones',
        children: [
          {
            type: 'link',
            label: 'Don Lucho (POS)',
            route: '/solutions/pos',
            icon: 'mat:insights',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 2,
            id_vista: 3
          },
          {
            type: 'link',
            label: 'Agente de llamada',
            route: '/solutions/natal_ia',
            icon: 'mat:insights',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 2,
            id_vista: 2
          },
        ]
      },
      {
        type: 'subheading',
        label: 'Inteligencia Artificial',
        children: [
          {
            type: 'link',
            label: 'Asistentes Virtuales',
            route: '/ia/assistants',
            icon: 'mat:insights',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 3,
            id_vista: 4
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Agency CIC',
        children: [
          {
            type: 'link',
            label: 'Licencias de Juego',
            route: '/agency/gaming-licenses/',
            icon: 'mat:insights',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 4,
            id_vista: 5
          },
          {
            type: 'link',
            label: 'Administración de tickets',
            route: '/agency/administracion-tickets',
            icon: 'mat:insights',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 4,
            id_vista: 6
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Consultas',
        children: [
          {
            type: 'link',
            label: 'AWS',
            route: '/consultas/aws',
            icon: 'mat:cloud',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 6,
            id_vista: 9
          },
          {
            type: 'link',
            label: 'Azure',
            route: '/consultas/azure',
            icon: 'mat:cloud_queue',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 6,
            id_vista: 10
          },
          {
            type: 'link',
            label: 'Productos',
            route: '/consultas/productos/',
            icon: 'mat:people',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 6,
            id_vista: 12
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Opciones',
        children: [
          {
            type: 'link',
            label: 'Soporte',
            route: '/options/soporte/',
            icon: 'mat:insights',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 5,
            id_vista: 7
          },
          {
            type: 'link',
            label: 'Administración',
            route: '/options/user/',
            icon: 'mat:insights',
            routerLinkActiveOptions: { exact: true },
            id_modulo: 5,
            id_vista: 8
          }
        ]
      }
    ];

    this.refreshNavigation();
  }

  // Aplica el filtro de permisos sobre _allItems y emite el resultado.
  // Si el caché está vacío (usuario no autenticado o permisos aún no cargados),
  // emite todos los ítems sin filtrar.
  refreshNavigation(): void {
    if (this.outlineService.cacheVacio()) {
      this._items.next(this._allItems);
      return;
    }
    this._items.next(this.filterByPermissions(this._allItems));
  }

  private filterByPermissions(items: NavigationItem[]): NavigationItem[] {
    return items.reduce((result: NavigationItem[], item) => {
      if (item.type === 'subheading') {
        const filteredChildren = this.filterChildren(item.children);
        if (filteredChildren.length > 0) {
          result.push({ ...item, children: filteredChildren });
        }
      } else {
        result.push(item);
      }
      return result;
    }, []);
  }

  private filterChildren(
    children: Array<NavigationLink | NavigationDropdown>
  ): Array<NavigationLink | NavigationDropdown> {
    return children.reduce(
      (result: Array<NavigationLink | NavigationDropdown>, child) => {
        if (child.type === 'link') {
          const { id_modulo, id_vista } = child;
          // Sin id_modulo/id_vista → siempre visible
          if (!id_modulo || !id_vista || this.outlineService.tienePermiso(id_modulo, id_vista, 'Ocultar')) {
            result.push(child);
          }
        } else if (child.type === 'dropdown') {
          const filteredChildren = this.filterChildren(child.children);
          if (filteredChildren.length > 0) {
            result.push({ ...child, children: filteredChildren });
          }
        }
        return result;
      },
      []
    );
  }
}
